import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from '@supabase/supabase-js';
import { useAuth } from "@/contexts/AuthProvider";
import Header from "@/components/layout/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BarChart3, 
  Globe, 
  Users, 
  Calendar, 
  Settings, 
  FileText, 
  TrendingUp 
} from "lucide-react";
import DashboardOverview from "@/components/dashboard/DashboardOverview";
import CountriesTab from "@/components/dashboard/CountriesTab";
import EmployeesTab from "@/components/dashboard/EmployeesTab";
import CalendarTab from "@/components/dashboard/CalendarTab";
import GovernanceTab from "@/components/dashboard/GovernanceTab";
import RequestsTab from "@/components/dashboard/RequestsTab";
import ReportsTab from "@/components/dashboard/ReportsTab";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
  maximum_points_per_employee?: number;
}

const CompanyDashboard = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
const { session, initialized } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

useEffect(() => {
    console.debug('[CompanyDashboard] auth check', { initialized, hasSession: !!session });
    if (!initialized) return;
    if (!session?.user) return;
    checkUserRole(session.user);
  }, [initialized, session]);

  const checkUserRole = async (user: User) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Check role from user_roles table
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'company')
      .single();

    if (!roleData) {
      navigate('/');
      return;
    }

    // Get profile to access company_id
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!profile) {
      navigate('/');
      return;
    }

    // Check if company request is approved
    const { data: companyData } = await supabase
      .from('companies')
      .select('*')
      .eq('admin_id', profile.id)
      .single();

    if (!companyData) {
      toast({
        variant: "destructive",
        title: "Accès refusé",
        description: "Votre demande d'entreprise n'a pas encore été approuvée"
      });
      navigate('/');
      return;
    }

    setCompany(companyData);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        <Header showLogoutButton={true} />
        <div className="container mx-auto py-16 px-4">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Header showLogoutButton={true} />
      <div className="container mx-auto py-8 px-4 pt-20 sm:pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Dashboard RH - {company?.name}
            </h1>
            <p className="text-muted-foreground">
              Pilotez votre équipe et le télétravail en temps réel
            </p>
          </div>

          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="sticky top-[4.5rem] z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border flex flex-wrap justify-start sm:justify-center w-full lg:w-auto mb-8 h-auto p-1 px-2 overflow-x-auto gap-2">
              <TabsTrigger value="overview" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Tableau de bord</span>
                <span className="sm:hidden">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger value="countries" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <Globe className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Mes Pays</span>
                <span className="sm:hidden">Pays</span>
              </TabsTrigger>
              <TabsTrigger value="employees" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Collaborateurs</span>
                <span className="sm:hidden">Équipe</span>
              </TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Calendrier</span>
                <span className="sm:hidden">Planning</span>
              </TabsTrigger>
              <TabsTrigger value="governance" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Nest Points</span>
                <span className="sm:hidden">Points</span>
              </TabsTrigger>
              <TabsTrigger value="requests" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Demandes</span>
                <span className="sm:hidden">Demandes</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm flex-shrink-0">
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Reporting</span>
                <span className="sm:hidden">Reports</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab Contents */}
            <TabsContent value="overview" className="mt-0">
              <DashboardOverview company={company} />
            </TabsContent>

            <TabsContent value="countries" className="mt-0">
              <CountriesTab company={company} />
            </TabsContent>

            <TabsContent value="employees" className="mt-0">
              <EmployeesTab company={company} />
            </TabsContent>

            <TabsContent value="calendar" className="mt-0">
              <CalendarTab company={company} />
            </TabsContent>

            <TabsContent value="governance" className="mt-0">
              <GovernanceTab company={company} />
            </TabsContent>

            <TabsContent value="requests" className="mt-0">
              <RequestsTab company={company} />
            </TabsContent>

            <TabsContent value="reports" className="mt-0">
              <ReportsTab company={company} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;